import { useMemo, type CSSProperties } from 'react';
import { useTheme } from '@course-authoring/shared-types';
import { useTranslation } from '@course-authoring/i18n';
import { useIsMobileViewport } from './hooks/useIsMobileViewport';

const FOOTER_LINK_CLASS = 'content-footer-link';

function createFooterHoverCSS(): string {
  return `
    .${FOOTER_LINK_CLASS}:hover,
    .${FOOTER_LINK_CLASS}:focus-visible {
      text-decoration: underline !important;
    }
  `;
}

export function ContentFooter() {
  const { tokens } = useTheme();
  const { t } = useTranslation();
  const isMobile = useIsMobileViewport(tokens.layout.mobileBreakpoint);
  const hoverCSS = useMemo(() => createFooterHoverCSS(), []);

  const styles = useMemo<Record<string, CSSProperties>>(
    () => ({
      footer: {
        borderTopWidth: '1px',
        borderTopStyle: 'solid',
        borderTopColor: tokens.borders.default,
        padding: `${tokens.spacing.md} ${tokens.spacing.lg}`,
        textAlign: 'center',
        fontFamily: tokens.typography.fontFamily,
        fontSize: isMobile ? tokens.typography.mobile.body.size : tokens.typography.sizes.base,
        fontWeight: tokens.typography.weights.regular,
        lineHeight: tokens.typography.lineHeights.tight,
        color: tokens.text.primary,
        ...(isMobile && {
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: tokens.spacing.xs,
        }),
      },
      separator: {
        margin: `0 ${tokens.spacing.sm}`,
      },
      link: {
        color: 'inherit',
        textDecoration: 'none',
      },
    }),
    [tokens, isMobile]
  );

  return (
    <>
      <style>{hoverCSS}</style>
      <footer style={styles.footer} data-testid="content-footer">
        <span>© {new Date().getFullYear()} Emtrain</span>
        {!isMobile && <span style={styles.separator} aria-hidden="true">|</span>}
        <span>
          <a href="https://emtrain.com/emtrain-privacy-policy/" target="_blank" rel="noopener noreferrer" style={styles.link} className={FOOTER_LINK_CLASS}>{t('content_footer.privacy')}</a>
          <span style={styles.separator} aria-hidden="true">|</span>
          <a href="https://emtrain.com/terms-of-service/" target="_blank" rel="noopener noreferrer" style={styles.link} className={FOOTER_LINK_CLASS}>{t('content_footer.terms_of_service')}</a>
          <span style={styles.separator} aria-hidden="true">|</span>
          <a href="https://emtrain.com/accessibility/" target="_blank" rel="noopener noreferrer" style={styles.link} className={FOOTER_LINK_CLASS}>{t('content_footer.accessibility')}</a>
        </span>
      </footer>
    </>
  );
}
